/**
 * Shared CEO dashboard layout tokens — aligned with BDM Dashboard.js
 */
import React from 'react';
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';

export const GRID_SPACING = 3;

/** BDM standard panel / KPI sizes */
export const BDM_KPI_HEIGHT = 140;
export const BDM_PANEL_HEIGHT = 420;
export const BDM_CHART_HEIGHT = 320;
export const BDM_TABLE_HEIGHT = 500;

export const BDM_CARD_SX = {
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: 3,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  overflow: 'hidden',
};

/** Wraps panel so Grid stretch does not inflate height (fixed px only). */
export const gridPanelItemSx = {
  display: 'flex',
  '& > *': {
    width: '100%',
  },
};

export function CeoSectionTitle({ children, subtitle }) {
  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ color: 'text.primary' }}
      >
        {children}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Full-width section divider with optional action (unified dashboard). */
export function DashboardSectionHeader({ title, subtitle, action, first = false }) {
  return (
    <Box
      sx={{
        mt: first ? 0 : 3,
        pt: first ? 0 : 3,
        mb: 1.5,
        borderTop: first ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    </Box>
  );
}

/**
 * Standard white panel — same structure as BDM chart cards (420px tall).
 */
export function CeoDashboardPanel({
  title,
  subtitle,
  icon: Icon,
  height = BDM_PANEL_HEIGHT,
  children,
  onClick,
  headerAction,
}) {
  const card = (
    <Card
      sx={{
        ...BDM_CARD_SX,
        height,
        maxHeight: height,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 2.5,
          '&:last-child': { pb: 2.5 },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1.5,
            flexShrink: 0,
          }}
        >
          {Icon && (
            <Icon
              sx={{
                fontSize: 28,
                color: 'primary.main',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerAction}
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>
      </CardContent>
    </Card>
  );

  if (onClick) {
    return (
      <CardActionArea onClick={onClick} sx={{ borderRadius: 3, height, maxHeight: height }}>
        {card}
      </CardActionArea>
    );
  }

  return card;
}

/** Fixed-size chart area for Nivo / MUI charts */
export function CeoChartSlot({ height = BDM_CHART_HEIGHT, children }) {
  return (
    <Box
      sx={{
        position: 'relative',
        height,
        minHeight: height,
        width: '100%',
        flexShrink: 0,
        flexGrow: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0 }}>{children}</Box>
    </Box>
  );
}
